interface Entity {
  name: string;
  type: string;
}

export class WowEntity implements Entity {
  readonly id: number;
  readonly name: string;
  readonly region: string;
  readonly realm: string;
  readonly type: string = "com.kos.entities.domain.WowEntityRequest";

  constructor(id: number, name: string, region: string, realm: string) {
    this.id = id;
    this.name = name;
    this.region = region;
    this.realm = realm;
  }
}

export class LolEntity implements Entity {
  readonly id: number;
  readonly name: string;
  readonly tag: string;
  readonly type: string = "com.kos.entities.domain.LolEntityRequest";

  constructor(id: number, name: string, tag: string) {
    this.id = id;
    this.name = name;
    this.tag = tag;
  }
}

interface EntityCreation {
  name: string;
  type: string;
}

export class WowEntityCreation implements EntityCreation {
  readonly name: string;
  readonly region: string;
  readonly realm: string;
  readonly type: string = "com.kos.entities.domain.WowEntityRequest";

  constructor(name: string, region: string, realm: string) {
    this.name = name;
    this.region = region;
    this.realm = realm;
  }
}

export class LolEntityCreation implements EntityCreation {
  readonly name: string;
  readonly tag: string;
  readonly type: string = "com.kos.entities.domain.LolEntityRequest";

  constructor(name: string, tag: string) {
    this.name = name;
    this.tag = tag;
  }
}

export interface View {
  id: String;
  name: string;
  owner: string;
  entities: Entity[];
  published: boolean;
  featured: boolean;
  game: string;
}

export interface ViewCreation {
  name: string;
  entities: EntityCreation[];
  published: boolean;
  featured: boolean;
  game: string;
}

export interface Metadata {
  totalCount: number;
}

export interface SimpleView {
  id: string;
  name: string;
  owner: string;
  entities: number[];
  published: boolean;
  featured: boolean;
  game: string;
}

export interface SimpleRow {
  key: string;
  viewName: string;
  viewOwner: string;
  viewUrl: string;
  published: boolean;
  featured: boolean;
}

const hostMap: { [key in SimpleView["game"]]: string | undefined } = {
  WOW: process.env.REACT_APP_WOW_FRONT_HOST,
  LOL: process.env.REACT_APP_LOL_FRONT_HOST,
  WOW_HC: process.env.REACT_APP_WOW_HC_FRONT_HOST,
};

export const simpleViewToRow = (sv: SimpleView) => {
  const host = hostMap[sv.game] || undefined;

  return {
    key: sv.id,
    viewName: sv.name,
    viewUrl: `${host}/${sv.id}`,
    viewOwner: sv.owner,
    published: sv.published,
    featured: sv.featured,
  };
};

export const viewToEditView = (v: View) => {
  return {
    name: v.name,
    entities: v.entities.map((entity) => {
      switch (v.game) {
        case "WOW":
          const wc = entity as WowEntity;
          return new WowEntityCreation(wc.name, wc.region, wc.realm);
        case "LOL":
          const lc = entity as LolEntity;
          return new LolEntityCreation(lc.name, lc.tag);
        case "WOW_HC":
          const whc = entity as WowEntity;
          return new WowEntityCreation(whc.name, whc.region, whc.realm);
        default:
          throw new Error("Invalid entity type");
      }
    }),
    published: v.published,
    featured: v.featured,
    game: v.game,
  };
};

export interface ViewsState {
  views: SimpleView[];
  currentView?: View;
  viewCreationInProgress: ViewCreation;
}

export interface UpdateSimpleEntityAction {
  index: number;
  entity: EntityCreation;
}

export interface UpdateEntityAction {
  index: number;
  entity: Entity;
}

export interface ViewModified {
  id: string;
  entities: number[];
  published: boolean;
  featured: boolean;
  isSuccess: boolean;
}

export interface ViewDeleted {
  viewId: string;
  isSuccess: boolean;
}

export const basicWowEntityRequest = {
  name: "new-character",
  region: "eu",
  realm: "zuljin",
  type: "com.kos.entities.domain.WowEntityRequest",
};

export const basicLolEntityRequest = {
  name: "new-summoner",
  tag: "EUW",
  type: "com.kos.entities.domain.LolEntityRequest",
};

export const basicCreationView = {
  name: "new-view",
  entities: [basicWowEntityRequest],
  published: true,
  featured: false,
  game: "WOW",
};

export const basicView = {
  id: "1",
  owner: "owner",
  name: "new-view",
  entities: [],
  published: true,
  featured: false,
  game: "WOW",
};

export const initialState: ViewsState = {
  views: [],
  currentView: undefined,
  viewCreationInProgress: basicCreationView,
};
